/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

export interface JobContext {
  jobId: string
  title: string
  siteAddress: string
  createdAt: string
  status: string
  nodeCount: number
  photoCount: number
  // Optional handlers for job actions (only provided when user can edit)
  onEditJob?: () => void
  onViewFiles?: () => void
  onDownloadMaps?: () => void
}

interface DrawerContextType {
  jobContext: JobContext | null
  setJobContext: (context: JobContext | null) => void
  gpsAccuracy: number | null
  setGpsAccuracy: (accuracy: number | null) => void
  /** Full-screen editor pages hide MainLayout's mobile header via this flag
      (CSS-driven; replaces the old header.style.display DOM mutation) */
  hideMobileHeader: boolean
  setHideMobileHeader: (hidden: boolean) => void
  /** Mobile nav drawer open state lives here so pages with their own chrome
      (JobDetail's floating pill) can open MainLayout's drawer */
  mobileNavOpen: boolean
  setMobileNavOpen: (open: boolean) => void
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined)

export const DrawerProvider = ({ children }: { children: ReactNode }) => {
  const [jobContext, setJobContext] = useState<JobContext | null>(null)
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null)
  const [hideMobileHeader, setHideMobileHeader] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  return (
    <DrawerContext.Provider
      value={{
        jobContext,
        setJobContext,
        gpsAccuracy,
        setGpsAccuracy,
        hideMobileHeader,
        setHideMobileHeader,
        mobileNavOpen,
        setMobileNavOpen,
      }}
    >
      {children}
    </DrawerContext.Provider>
  )
}

/** Hide MainLayout's mobile header while the calling page is mounted. */
export const useHideMobileHeader = () => {
  const { setHideMobileHeader } = useDrawerContext()
  useEffect(() => {
    setHideMobileHeader(true)
    return () => setHideMobileHeader(false)
  }, [setHideMobileHeader])
}

export const useDrawerContext = () => {
  const context = useContext(DrawerContext)
  if (!context) {
    throw new Error('useDrawerContext must be used within DrawerProvider')
  }
  return context
}
